import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { updateProduct } from "../../../RTK/EditProductSlice";
import { toast } from "react-toastify";
import {
  ImagePlus,
  Save,
  XCircle,
  Sparkles,
  Package,
  DollarSign,
  Tag,
  FileText,
  Eye,
  EyeOff,
  PenSquare,
  Percent,
  ClipboardCheck,
  Loader2,
  FolderKanban,
  Zap,
  Shield,
  Hash,
  Plus,
  Minus,
  ChevronDown,
} from "lucide-react";
import axios from "axios";
import { fetchProductById } from "../../../RTK/productSlice";

const EditProductPopup = ({ product, setShowEditProductPopup }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    image: product?.image || "",
    name: product?.name || "",
    price: product?.price || "",
    description: product?.description || "",
    product_type: product?.product_type || "",
    commission_rate: product?.commission_rate || "",
    is_hidden: product?.is_hidden || false,
    status: product?.status === "available",
    category: product?.category?.[0]?.id || "",
    // New fields
    player_check: product?.player_check || false,
    is_instant: product?.is_instant || false,
    product_requirements: product?.product_requirements?.required || [],
    qty_min: product?.qty_values?.min || "",
    qty_max: product?.qty_values?.max || "",
    is_fixed_price: product?.is_fixed_price || false,
    fixed_cost: product?.fixed_cost ?? "",
  });
  const [loading, setLoading] = useState(false);
  const [newRequirement, setNewRequirement] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [commissionError, setCommissionError] = useState(false);

  // قائمة الاقتراحات المطلوبة
  const requirementSuggestions = [
    { label: "معرف اللاعب ", value: "playerid" },
    { label: "بريد الكتروني", value: "email" },
    { label: "كلمة سر ", value: "password" },
    { label: "رابط ", value: "url" },
    { label: "السيرفر ", value: "serverid" },
    { label: "اسم المستخدم ", value: "username" },
    { label: "رقم الهاتف ", value: "phone" },
    // { label: "معرف الحساب ", value: "userid" },
    // { label: "كود التفعيل ", value: "code" },
    // { label: "اسم الشخصية - character", value: "character" },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (name === "commission_rate" && value && parseFloat(value) > 0) {
      setCommissionError(false);
    }
  };

  // Add requirement
  const addRequirement = () => {
    if (newRequirement.trim()) {
      setFormData(prev => ({
        ...prev,
        product_requirements: [...prev.product_requirements, newRequirement.trim()]
      }));
      setNewRequirement(""); // Clear the input after adding
      setShowSuggestions(false); // إخفاء الاقتراحات بعد الإضافة
    }
  };

  // Add requirement from suggestion
  const addSuggestionRequirement = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      product_requirements: [...prev.product_requirements, suggestion.value]
    }));
    setNewRequirement(""); // Clear the input after adding
    setShowSuggestions(false); // إخفاء الاقتراحات بعد الإضافة
  };

  // Remove requirement
  const removeRequirement = (index) => {
    setFormData(prev => ({
      ...prev,
      product_requirements: prev.product_requirements.filter((_, i) => i !== index)
    }));
  };

  // باقي الكود نفسه...
  useEffect(() => {
    // Cleanup function to revoke object URL when component unmounts
    return () => {
      if (formData.image && formData.image.startsWith('blob:')) {
        URL.revokeObjectURL(formData.image);
      }
    };
  }, [formData.image]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.is_fixed_price) {
      if (!formData.fixed_cost || parseFloat(formData.fixed_cost) <= 0) {
        toast.error("يجب إدخال التكلفة الثابتة عند تفعيل السعر الثابت");
        return;
      }
      if (!formData.commission_rate || parseFloat(formData.commission_rate) <= 0) {
        setCommissionError(true);
        return;
      }
      setCommissionError(false);
    } else {
      setCommissionError(false);
    }

    setLoading(true);
  
    try {
      const form = new FormData();
      
      // Handle all form fields - same as before
      Object.keys(formData).forEach(key => {
        if (key === "status") {
          form.append(key, formData[key] ? "available" : "unavailable");
        } else if (key === "imageFile" && formData[key]) {
          form.append("image", formData[key]);
        } else if (key === "product_requirements") {
          form.append("product_requirements", JSON.stringify({
            required: formData[key]
          }));
        } else if (key === "qty_min" || key === "qty_max") {
          if (key === "qty_min") {
            form.append("qty_values", JSON.stringify({
              min: formData.qty_min,
              max: formData.qty_max
            }));
          }
        } else if (key === "is_fixed_price") {
          form.append(key, formData[key] ? "true" : "false");
        } else if (key === "fixed_cost") {
          if (formData.is_fixed_price && formData.fixed_cost !== "") {
            form.append(key, formData.fixed_cost);
          } else {
            form.append(key, "");
          }
        } else if (key !== "image" && key !== "qty_max") {
          form.append(key, formData[key]);
        }
      });
  
      if (formData.imageFile) {
        form.append("image", formData.imageFile);
      }
  
      // Update product
      await dispatch(updateProduct({ id: product.id, formData: form })).unwrap();
      
      // Refresh product details immediately
      await dispatch(fetchProductById(product.id));
      
      toast.success("تم تحديث المنتج بنجاح");
      setShowEditProductPopup(false);
    } catch (error) {
      console.error("Error updating product:", error);
      toast.error("فشل تحديث المنتج");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create a preview URL for the image
      const previewUrl = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        image: previewUrl,
        imageFile: file  // Store the actual file
      }));
    }
  };

  const handleGenerateDescription = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/Products/GenerateDescription/`,
        { name: formData.name }, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      setFormData((prev) => ({
        ...prev,
        description: response.data.description,
      }));
      toast.success("تم إنشاء الوصف بنجاح");
    } catch (error) {
      toast.error("فشل إنشاء الوصف");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-lg z-[4000]" />

      <div
        className="fixed inset-0 z-[50000] flex items-center justify-center p-4 sm:p-6"
        dir="rtl"
      >
        <div className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl bg-gray-900 shadow-xl">
          {/* Header */}
          <div className="sticky top-0 z-20 bg-gray-900/95 backdrop-blur border-b border-gray-800 px-6 py-4">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-white">
                <PenSquare className="w-5 h-5 text-amber-500" />
                تعديل المنتج
              </h2>
              <button
                onClick={() => setShowEditProductPopup(false)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-8">
              {/* Image Upload */}
              <div className="flex justify-center">
                <div className="relative group">
                  <div className="relative w-32 h-32 rounded-2xl overflow-hidden bg-gray-800 border-2 border-gray-700 group-hover:border-amber-500 transition-colors">
                    <img
                      src={formData.image || "/placeholder.jpg"}
                      alt="صورة المنتج"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-colors" />
                    <label
                      htmlFor="imageUpload"
                      className="absolute inset-0 flex items-center justify-center cursor-pointer"
                    >
                      <ImagePlus className="w-6 h-6 text-white opacity-70 group-hover:opacity-100 transition-opacity" />
                    </label>
                  </div>
                  <input
                    type="file"
                    id="imageUpload"
                    name="image"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageChange}
                  />
                </div>
              </div>

              {/* Basic Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Name */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-400">
                    <Tag className="w-4 h-4" />
                    اسم المنتج
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 focus:border-amber-500 text-white rounded-lg focus:ring-2 focus:ring-amber-500/20 transition-colors text-right"
                  />
                </div>

                {/* Price */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-400">
                    <DollarSign className="w-4 h-4" />
                    السعر
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 focus:border-amber-500 text-white rounded-lg focus:ring-2 focus:ring-amber-500/20 transition-colors text-right"
                  />
                </div>

                {/* Category */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-400">
                    <FolderKanban className="w-4 h-4" />
                    التصنيف
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 focus:border-amber-500 text-white rounded-lg focus:ring-2 focus:ring-amber-500/20 transition-colors appearance-none text-right"
                  >
                    <option value="" disabled>
                      اختر التصنيف
                    </option>
                    {product.categories?.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-400">
                  <FileText className="w-4 h-4" />
                  الوصف
                </label>
                <div className="space-y-2">
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 focus:border-amber-500 text-white rounded-lg focus:ring-2 focus:ring-amber-500/20 transition-colors resize-none text-right"
                  />
                  <button
                    type="button"
                    onClick={handleGenerateDescription}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-gray-900 font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                    إنشاء وصف تلقائي
                  </button>
                </div>
              </div>

              {/* Product Configuration Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Product Type */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-400">
                    <Package className="w-4 h-4" />
                    نوع المنتج
                  </label>
                  <select
                    name="product_type"
                    value={formData.product_type}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 focus:border-amber-500 text-white rounded-lg focus:ring-2 focus:ring-amber-500/20 transition-colors appearance-none text-right"
                  >
                    <option value="" disabled>
                      اختر النوع
                    </option>
                    <option value="quantity">كمية</option>
                    <option value="package">حزمة</option>
                    <option value="stock">مخزون</option>
                  </select>
                </div>

                {/* Commission Rate */}
                <div className="space-y-2">
                  <label className={`flex items-center gap-2 text-sm font-medium ${commissionError ? "text-red-400" : "text-gray-400"}`}>
                    <Percent className="w-4 h-4" />
                    نسبة العمولة
                    {formData.is_fixed_price && (
                      <span className={`text-xs ${commissionError ? "text-red-400" : "text-amber-400"}`}>(مطلوبة)</span>
                    )}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="commission_rate"
                    value={formData.commission_rate}
                    onChange={handleChange}
                    className={`w-full px-4 py-2.5 bg-gray-800 text-white rounded-lg transition-colors text-right ${
                      commissionError
                        ? "border-2 border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
                        : "border border-gray-700 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                    }`}
                  />
                </div>

                {/* Status */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-400">
                    <ClipboardCheck className="w-4 h-4" />
                    الحالة
                  </label>
                  <select
                    name="status"
                    value={formData.status ? "available" : "unavailable"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        status: e.target.value === "available",
                      }))
                    }
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 focus:border-amber-500 text-white rounded-lg focus:ring-2 focus:ring-amber-500/20 transition-colors appearance-none text-right"
                  >
                    <option value="available">متاح</option>
                    <option value="unavailable">غير متاح</option>
                  </select>
                </div>

                {/* Visibility Toggle - Fixed */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-400">
                    <Eye className="w-4 h-4" />
                    رؤية المنتج
                  </label>
                  <div className="flex items-center justify-between px-4 py-2.5 bg-gray-800 rounded-lg border border-gray-700">
                    <div className="flex items-center gap-2">
                      {formData.is_hidden ? (
                        <EyeOff className="w-4 h-4 text-red-400" />
                      ) : (
                        <Eye className="w-4 h-4 text-green-400" />
                      )}
                      <span className="text-sm text-white">
                        {formData.is_hidden ? "مخفي" : "مرئي"}
                      </span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="is_hidden"
                        checked={formData.is_hidden}
                        onChange={handleChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Fixed Price Section */}
              <div className="space-y-4 p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                <h3 className="flex items-center gap-2 text-sm font-medium text-gray-300">
                  <DollarSign className="w-4 h-4 text-amber-500" />
                  التسعير الثابت
                </h3>

                <div className="flex items-center justify-between px-4 py-2.5 bg-gray-800 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-2">
                    <DollarSign className={`w-4 h-4 ${formData.is_fixed_price ? "text-amber-400" : "text-gray-400"}`} />
                    <span className="text-sm text-white">
                      {formData.is_fixed_price ? "مفعّل" : "غير مفعّل"}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_fixed_price"
                      checked={formData.is_fixed_price}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFormData((prev) => ({
                          ...prev,
                          is_fixed_price: checked,
                          fixed_cost: checked ? prev.fixed_cost : "",
                        }));
                        if (!checked) setCommissionError(false);
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                  </label>
                </div>

                {formData.is_fixed_price && (
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-400">
                      <DollarSign className="w-4 h-4" />
                      التكلفة الثابتة
                    </label>
                    <input
                      type="number"
                      step="0.0001"
                      name="fixed_cost"
                      value={formData.fixed_cost}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 focus:border-amber-500 text-white rounded-lg focus:ring-2 focus:ring-amber-500/20 transition-colors text-right"
                      placeholder="مثال: 50"
                    />
                  </div>
                )}

                <div className="text-xs text-amber-400/90 bg-amber-500/10 border border-amber-500/20 p-3 rounded-lg leading-relaxed">
                  <p className="font-medium mb-1">ملاحظة مهمة:</p>
                  <ul className="list-disc list-inside space-y-1 mr-2">
                    <li>عند تفعيل السعر الثابت، سيتم تطبيقه على جميع المستخدمين كسعر موحد.</li>
                    <li>سيتم استثناء نسب المستويات من هذا السعر.</li>
                    <li>حقل التكلفة الثابتة يمثل تكلفة المنتج، لذلك نسبة العمولة ضرورية لحساب السعر النهائي.</li>
                  </ul>
                </div>
              </div>

              {/* Product Features */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Player Check Toggle */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-400">
                    <Shield className="w-4 h-4" />
                    فحص اللاعب
                  </label>
                  <div className="flex items-center justify-between px-4 py-2.5 bg-gray-800 rounded-lg border border-gray-700">
                    <div className="flex items-center gap-2">
                      <Shield className={`w-4 h-4 ${formData.player_check ? 'text-green-400' : 'text-gray-400'}`} />
                      <span className="text-sm text-white">
                        {formData.player_check ? "مفعل" : "غير مفعل"}
                      </span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="player_check"
                        checked={formData.player_check}
                        onChange={handleChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                    </label>
                  </div>
                </div>

                {/* Instant Toggle */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-400">
                    <Zap className="w-4 h-4" />
                    تسليم فوري
                  </label>
                  <div className="flex items-center justify-between px-4 py-2.5 bg-gray-800 rounded-lg border border-gray-700">
                    <div className="flex items-center gap-2">
                      <Zap className={`w-4 h-4 ${formData.is_instant ? 'text-yellow-400' : 'text-gray-400'}`} />
                      <span className="text-sm text-white">
                        {formData.is_instant ? "فوري" : "عادي"}
                      </span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="is_instant"
                        checked={formData.is_instant}
                        onChange={handleChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Quantity Range */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-400">
                    <Hash className="w-4 h-4" />
                    الحد الأدنى للكمية
                  </label>
                  <input
                    type="number"
                    name="qty_min"
                    value={formData.qty_min}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 focus:border-amber-500 text-white rounded-lg focus:ring-2 focus:ring-amber-500/20 transition-colors text-right"
                    placeholder="50"
                  />
                </div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-400">
                    <Hash className="w-4 h-4" />
                    الحد الأقصى للكمية
                  </label>
                  <input
                    type="number"
                    name="qty_max"
                    value={formData.qty_max}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 focus:border-amber-500 text-white rounded-lg focus:ring-2 focus:ring-amber-500/20 transition-colors text-right"
                    placeholder="100000"
                  />
                </div>
              </div>

              {/* Product Requirements - محسن مع الاقتراحات */}
              <div className="space-y-4">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-400">
                  <ClipboardCheck className="w-4 h-4" />
                  متطلبات المنتج
                </label>
                
                <div className="space-y-3">
                  {/* عرض المتطلبات الحالية */}
                  {formData.product_requirements && formData.product_requirements.length > 0 && (
                    <div className="space-y-2">
                      {formData.product_requirements.map((requirement, index) => (
                        <div key={index} className="flex items-center gap-2 p-3 bg-gray-800 rounded-lg border border-gray-700">
                          <ClipboardCheck className="w-4 h-4 text-green-400 flex-shrink-0" />
                          <span className="flex-1 text-white text-sm">{requirement}</span>
                          <button
                            type="button"
                            onClick={() => removeRequirement(index)}
                            className="p-1 text-red-400 hover:text-red-300 transition-colors flex-shrink-0"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* إضافة متطلب جديد مع الاقتراحات */}
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          value={newRequirement}
                          onChange={(e) => setNewRequirement(e.target.value)}
                          placeholder="أضف متطلب جديد أو اختر من الاقتراحات"
                          className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 focus:border-amber-500 text-white rounded-lg focus:ring-2 focus:ring-amber-500/20 transition-colors text-right"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addRequirement();
                            }
                          }}
                          onFocus={() => setShowSuggestions(true)}
                        />
                      </div>
                      
                      <button
                        type="button"
                        onClick={() => setShowSuggestions(!showSuggestions)}
                        className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-1"
                        title="عرض الاقتراحات"
                      >
                        <ChevronDown className={`w-4 h-4 transform transition-transform ${showSuggestions ? 'rotate-180' : ''}`} />
                        اقتراحات
                      </button>
                      
                      <button
                        type="button"
                        onClick={addRequirement}
                        disabled={!newRequirement.trim()}
                        className="px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        إضافة
                      </button>
                    </div>

                    {/* قائمة الاقتراحات */}
                    {showSuggestions && (
                      <div className="bg-gray-800 border border-gray-700 rounded-lg max-h-60 overflow-y-auto">
                        <div className="p-2 border-b border-gray-700">
                          <h4 className="text-sm font-medium text-gray-400 flex items-center gap-2">
                            <ClipboardCheck className="w-4 h-4" />
                            اختر من الاقتراحات المتاحة
                          </h4>
                        </div>
                        <div className="p-2 space-y-1">
                          {requirementSuggestions.map((suggestion, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => addSuggestionRequirement(suggestion)}
                              disabled={formData.product_requirements.includes(suggestion.value)}
                              className={`w-full text-right p-3 rounded-lg transition-colors flex items-center justify-between group ${
                                formData.product_requirements.includes(suggestion.value)
                                  ? 'bg-gray-700 text-gray-300 cursor-not-allowed'
                                  : 'hover:bg-gray-700 text-white hover:border-amber-500'
                              }`}
                            >
                              <span className="text-sm">{suggestion.label}</span>
                              {formData.product_requirements.includes(suggestion.value) ? (
                                <span className="text-xs text-white bg-green-600 px-2 py-1 rounded-full">مضاف</span>
                              ) : (
                                <Plus className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                              )}
                            </button>
                          ))}
                        </div>
                        <div className="p-2 border-t border-gray-700">
                          <button
                            type="button"
                            onClick={() => setShowSuggestions(false)}
                            className="w-full text-center text-xs text-gray-400 hover:text-gray-300 transition-colors p-1"
                          >
                            إغلاق الاقتراحات
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* معلومات مساعدة */}
                  {/* <div className="text-xs text-gray-500 bg-gray-800/50 p-3 rounded-lg">
                    <p className="mb-1">💡 نصائح:</p>
                    <ul className="list-disc list-inside space-y-1 mr-4">
                      <li>اختر من الاقتراحات الجاهزة أو أضف متطلبات مخصصة</li>
                      <li>القيم المرسلة للباك إند ستكون باللغة الإنجليزية</li>
                      <li>يمكنك إضافة عدة متطلبات حسب نوع المنتج</li>
                    </ul>
                  </div> */}
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="sticky bottom-0 z-20 mt-8 -mx-6 -mb-6 px-6 py-4 bg-gray-900/95 backdrop-blur border-t border-gray-800">
              <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-start sm:space-x-reverse sm:space-x-2 gap-2">
                <button
                  type="button"
                  onClick={() => setShowEditProductPopup(false)}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 mt-3 sm:mt-0 bg-gray-800 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
                >
                  <XCircle className="w-4 h-4" />
                  إلغاء
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-gray-900 font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      جاري الحفظ...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      حفظ التغييرات
                    </>
                  )}
                </button>
                {commissionError && formData.is_fixed_price && (
                  <p className="text-sm text-red-400 font-medium sm:mr-2">
                    يجب إضافة نسبة العمولة عند تفعيل السعر الثابت
                  </p>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditProductPopup;